import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { ArrowLeft, LogOut, DoorOpen, CalendarIcon } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { API, pageVariants, cleanEmployeeName } from './utils';
import { playCheckoutFoundWithName, getTimePeriod, playVoiceMessage, stopAllAudio, getTimeBasedGreeting, getCurrentLanguage } from './voiceUtils';
import { TimeSelector } from './TimeSelector';
import { useGuestPortal } from './GuestPortalContext';

export function CheckOutForm({ setView, setSuccessMessage }) {
  const { t, language } = useGuestPortal();
  const [roomNumber, setRoomNumber] = useState("");
  const [employeeNumber, setEmployeeNumber] = useState("");
  const [date, setDate] = useState(new Date());
  const getCurrentTime = () => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  };
  const [time, setTime] = useState(getCurrentTime());
  const [loading, setLoading] = useState(false);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(true);
  const [verifiedBooking, setVerifiedBooking] = useState(null);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const response = await axios.get(`${API}/rooms`);
        setAvailableRooms(response.data || []);
      } catch (error) {
        console.error("Failed to fetch rooms:", error);
        toast.error("Failed to load room list");
      } finally {
        setLoadingRooms(false);
      }
    };
    fetchRooms();
  }, []);

  const handleVerifyCheckout = async () => {
    if (!roomNumber) {
      toast.error("Please enter room number");
      return;
    }
    setVerifying(true);
    try {
      const response = await axios.get(`${API}/lookup-room/${roomNumber.trim()}`);
      setEmployeeNumber(response.data.employee_number);
      const cleanedName = cleanEmployeeName(response.data.employee_name);
      setVerifiedBooking({...response.data, employee_name: cleanedName});
      toast.success(`Found: ${cleanedName} in Room ${roomNumber}`);
      setTime(getCurrentTime());
      playCheckoutFoundWithName(cleanedName);
    } catch (error) {
      toast.error(error.response?.data?.detail || "No active booking found for this room.");
      setVerifiedBooking(null);
      setEmployeeNumber("");
    } finally {
      setVerifying(false);
    }
  };

  const handleClearVerification = () => {
    setVerifiedBooking(null);
    setRoomNumber("");
    setEmployeeNumber("");
  };

  const handleCheckOut = async () => {
    if (!verifiedBooking) {
      toast.error("Please verify your room and employee number first");
      return;
    }
    if (!date) {
      toast.error("Please select check-out date");
      return;
    }
    if (!time) {
      toast.error("Please select check-out time");
      return;
    }

    let adjustedDate = new Date(date);
    const [hours, minutes] = time.split(':').map(Number);
    const checkoutDateTime = new Date(adjustedDate);
    checkoutDateTime.setHours(hours, minutes, 0, 0);
    
    if (verifiedBooking.check_in_date && verifiedBooking.check_in_time) {
      const checkInDate = new Date(verifiedBooking.check_in_date);
      const [ciHours, ciMinutes] = verifiedBooking.check_in_time.split(':').map(Number);
      checkInDate.setHours(ciHours, ciMinutes, 0, 0);
      if (checkoutDateTime <= checkInDate) {
        adjustedDate.setDate(adjustedDate.getDate() + 1);
        toast.info(`Date auto-adjusted to ${format(adjustedDate, "MMM d, yyyy")} (after midnight checkout)`);
      }
    }
    
    const now = new Date();
    const currentHour = now.getHours();
    if (currentHour >= 0 && currentHour < 6 && hours >= 0 && hours < 6) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selectedDateOnly = new Date(date);
      selectedDateOnly.setHours(0, 0, 0, 0);
      if (selectedDateOnly < today) {
        adjustedDate = new Date(today);
        adjustedDate.setHours(hours, minutes, 0, 0);
        toast.info(`Date corrected to today ${format(adjustedDate, "MMM d, yyyy")} (after midnight)`);
      }
    }

    setLoading(true);
    try {
      stopAllAudio();
      // Auto-retry on transient errors (network / 503 / 504) — protects guests
      // during brief MongoDB Atlas primary failovers.
      const MAX_ATTEMPTS = 3;
      let lastErr = null;
      let succeeded = false;
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
          await axios.post(`${API}/checkout`, {
            room_number: roomNumber.trim(),
            employee_number: employeeNumber.trim(),
            check_out_date: format(adjustedDate, "yyyy-MM-dd"),
            check_out_time: time
          }, { timeout: 20000 });
          succeeded = true;
          break;
        } catch (err) {
          lastErr = err;
          const status = err?.response?.status;
          const isTransient = !err.response
            || status === 503 || status === 504 || status === 502
            || err.code === 'ECONNABORTED' || err.message === 'Network Error';
          if (!isTransient || attempt === MAX_ATTEMPTS) throw err;
          if (attempt === 1) {
            toast.info(language === 'es'
              ? 'Sincronizando con la base de datos, un momento...'
              : 'Syncing with database, one moment…');
          }
          await new Promise(r => setTimeout(r, 1500 * attempt));
        }
      }
      if (!succeeded) throw lastErr;
      
      const timePeriod = getTimePeriod();
      setTimeout(() => playVoiceMessage(`checkout_${timePeriod}`), 500);
      
      const greeting = getTimeBasedGreeting();
      setSuccessMessage({
        title: `${greeting}!`,
        message: t.thankYouForStaying,
        subMessage: t.haveSafeJourney,
        keyReminder: t.keyReminder,
        type: "checkout"
      });
      setView("checkout-success");
      
      setTimeout(() => {
        stopAllAudio();
        setView("menu");
      }, 10000);
    } catch (error) {
      const errorDetail = error.response?.data?.detail || "Check-out failed";
      if (errorDetail.startsWith("EARLY_CHECKOUT_BLOCKED|")) {
        const hoursRemaining = parseFloat(errorDetail.split("|")[1]);
        const hoursText = hoursRemaining < 1 
          ? `${Math.round(hoursRemaining * 60)} minutes` 
          : `${hoursRemaining.toFixed(1)} hours`;
        toast.error(
          language === 'es'
            ? `No puede hacer checkout todavía. Debe esperar ${hoursText} más. Si necesita cambiar de habitación, escriba una nota en la tarjeta amarilla y colóquela en la ranura. El administrador realizará el cambio.`
            : `Cannot checkout yet. You must wait ${hoursText} more. If you need to change rooms, please write a note on the yellow card and place it in the slot. Admin will make the change.`,
          { duration: 10000 }
        );
      } else {
        toast.error(errorDetail);
      }
    } finally {
      setLoading(false);
    }
  };

  const currentLanguage = getCurrentLanguage();

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="w-full max-w-md"
    >
      <Card className="glass-card p-8" data-testid="checkout-form-card">
        <CardHeader className="pb-6">
          <button 
            onClick={() => setView("menu")} 
            className="text-vault-text-secondary hover:text-vault-gold transition-colors mb-4 flex items-center gap-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            {t.back}
          </button>
          <CardTitle className="font-outfit text-2xl font-bold text-vault-text tracking-tight flex items-center gap-3">
            <LogOut className="w-6 h-6 text-vault-gold" />
            {t.guestCheckOut}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {!verifiedBooking ? (
            <>
              <div>
                <label className="vault-label text-lg font-bold">{t.roomNumber}</label>
                <div className="relative">
                  <DoorOpen className="absolute left-3 top-1/2 -translate-y-1/2 w-6 h-6 text-vault-gold" />
                  <Input
                    value={roomNumber}
                    onChange={(e) => setRoomNumber(e.target.value)}
                    placeholder={t.tapToEnterRoom}
                    className="vault-input pl-12 input-highlight text-xl font-bold h-14 border-2 border-vault-gold"
                    data-testid="checkout-room-input"
                    autoFocus
                  />
                </div>
                <p className="text-vault-gold text-sm mt-2 font-medium text-center">
                  {currentLanguage === 'es' ? '⬆️ Ingrese el número de su habitación arriba' : '⬆️ Enter your room number above'}
                </p>
              </div>
              <Button
                onClick={handleVerifyCheckout}
                disabled={verifying}
                className="w-full vault-btn-primary h-12"
                data-testid="verify-checkout-btn"
              >
                {verifying ? t.findingBooking : t.findMyBooking}
              </Button>
            </>
          ) : (
            <>
              <div className="bg-emerald-900/30 border border-emerald-600/50 rounded-lg p-4">
                <p className="text-emerald-400 text-xs uppercase tracking-wide mb-1">{t.bookingFound}</p>
                <p className="text-vault-text font-bold text-lg">{verifiedBooking.employee_name}</p>
                <p className="text-vault-text-secondary text-sm">{t.employeeId}: {verifiedBooking.employee_number}</p>
                <p className="text-vault-text-secondary text-sm">{t.room}: {verifiedBooking.room_number}</p>
                <p className="text-vault-text-secondary text-sm">{t.checkedIn}: {verifiedBooking.check_in_date} at {verifiedBooking.check_in_time}</p>
                <button onClick={handleClearVerification} className="text-vault-text-secondary hover:text-vault-gold text-xs underline mt-2">
                  {t.notYourBooking}
                </button>
              </div>
              <div>
                <label className="vault-label">{t.checkOutDate}</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn("w-full vault-input justify-start text-left font-normal", !date && "text-muted-foreground")}
                      data-testid="checkout-date-btn"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 text-vault-text-secondary" />
                      {date ? format(date, "dd MMM yyyy") : t.checkOutDate}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-vault-surface border-vault-border" align="start">
                    <Calendar mode="single" selected={date} onSelect={setDate} initialFocus className="bg-vault-surface text-vault-text" data-testid="checkout-calendar" />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <label className="vault-label">{t.onDutyTime}</label>
                <TimeSelector value={time} onChange={setTime} dataTestId="checkout-time-selector" />
              </div>
              <Button
                onClick={handleCheckOut}
                disabled={loading}
                className="w-full vault-btn-primary h-12"
                data-testid="submit-checkout-btn"
              >
                {loading ? t.processing : t.completeCheckOut}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}
